import BoxOfficeReport from 'src/models/box-office-report/BoxOfficeReport'
import Generic from 'src/models/generic/Generic'
import Model, { executeMutation, GqlError, ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'
import StudioSettlement from 'src/models/settlement/StudioSettlement'

export const iconForState = function (state) {
  switch (state) {
    case 'pending':
      return {
        name: 'pending',
        color: 'grey',
        tooltip: 'Pending'
      }
    case 'open':
      return {
        name: 'schedule',
        color: 'grey',
        tooltip: 'Open'
      }
    case 'processing':
      return {
        name: 'browse_gallery',
        color: 'blue',
        tooltip: 'Processing'
      }
    case 'submitted':
      return {
        name: 'send',
        color: 'blue',
        tooltip: 'Submitted'
      }
    case 'paid':
      return {
        name: 'paid',
        color: 'positive',
        tooltip: 'Paid'
      }
    case 'finalized':
      return {
        name: 'check_circle',
        color: 'positive',
        tooltip: 'Finalized'
      }
    case 'voided':
      return {
        name: 'cancel',
        color: 'negative',
        tooltip: 'Voided'
      }
    default:
      throw new Error('Unhandled state ' + state)
  }
}

export const TYPES = {
  DEPOSIT: 'deposit',
  INITIAL: 'initial',
  ADJUSTMENT: 'adjustment',
  MANUAL: 'manual'
}
TYPES.ALL = _.values(TYPES)

export const STATES = {
  PENDING: 'pending',
  OPEN: 'open',
  PROCESSING: 'processing',
  SUBMITTED: 'submitted',
  PAID: 'paid',
  FINALIZED: 'finalized',
  VOIDED: 'voided'
}
STATES.ALL = _.values(STATES)

export const RENTAL_SOURCES = {
  NONE: 'none',
  DEPOSIT: 'deposit',
  MANUAL: 'manual',
  PERCENTAGE: 'percentage',
  FLAT_FEE: 'flat_fee',
  MINIMUM_GUARANTEE: 'minimum_guarantee'
}
RENTAL_SOURCES.ALL = _.values(RENTAL_SOURCES)

export const ACTIONS = {
  SETTLE: 'settle',
  UNSETTLE: 'unsettle',
  VOID: 'void'
}
ACTIONS.ALL = _.values(ACTIONS)

export const MODES = {
  WEEK_OF: 'week_of',
  BATCHED_AT: 'batched_at',
  PAID_AT: 'paid_at'
}
MODES.ALL = _.values(MODES)

export const BOX_OFFICE_SETTLEMENT_MODE_OPTIONS = [
  { label: 'Week Of', value: MODES.WEEK_OF },
  { label: 'Batched At', value: MODES.BATCHED_AT },
  { label: 'Paid At', value: MODES.PAID_AT }
]

export default class BoxOfficeReportSettlement extends Model {
  static name = 'BoxOfficeReportSettlement'

  static paginate = true
  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.FilmSettlement]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },

      type: {
        type: String,
        output: true,
        default: TYPES.MANUAL
      },
      sequence: {
        type: Integer,
        output: true,
        default: 0
      },
      identifier: {
        type: String,
        output: true,
        default: ''
      },
      description: {
        type: String,
        output: true,
        default: ''
      },
      state: {
        type: String,
        output: true,
        default: STATES.PENDING
      },
      rentalSource: {
        type: String,
        output: true,
        default: RENTAL_SOURCES.NONE
      },
      rentalValueHundredths: {
        type: Number,
        output: true,
        default: 0
      },
      locked: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      },

      boxOfficeReport: {
        type: BoxOfficeReport,
        output: true
      },
      studioSettlement: {
        type: StudioSettlement,
        output: true
      },

      netSales: {
        type: Number,
        output: true,
        default: 0
      },
      totalDue: {
        type: Number,
        output: true,
        default: 0
      },

      amount: {
        type: Number,
        output: true,
        default: 0
      },

      action: {
        type: String,
        input: true,
        default: null
      }
    }

    this.computed = {
      rentalValue () {
        return this.rentalValueHundredths / 100.0
      },
      rentalShort () {
        switch (this.rentalSource) {
          case RENTAL_SOURCES.NONE:
            return null
          case RENTAL_SOURCES.DEPOSIT:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')}D`
          case RENTAL_SOURCES.MANUAL:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')}M`
          case RENTAL_SOURCES.PERCENTAGE:
            return `${this.rentalValue}%`
          case RENTAL_SOURCES.FLAT_FEE:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')}F`
          case RENTAL_SOURCES.MINIMUM_GUARANTEE:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')}G`
          default:
            // throw new Error('Unhandled rental source ' + this.rentalSource)
            return null
        }
      },
      rentalLong () {
        switch (this.rentalSource) {
          case RENTAL_SOURCES.NONE:
            return null
          case RENTAL_SOURCES.DEPOSIT:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')} Deposit`
          case RENTAL_SOURCES.MANUAL:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')} Manual`
          case RENTAL_SOURCES.PERCENTAGE:
            return `${this.rentalValue}% Box Office Percentage`
          case RENTAL_SOURCES.FLAT_FEE:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')} Flat Fee`
          case RENTAL_SOURCES.MINIMUM_GUARANTEE:
            return `${this.i18n.n(this.rentalValue, 'currencyNoCents')} Minimum Guarantee`
          default:
            // throw new Error('Unhandled rental source ' + this.rentalSource)
            return null
        }
      }
    }

    this.mutations = {
      batchBoxOfficeReportSettlement: {
        arguments: {
          batchBy: {
            type: JsonObject,
            required: true
          },
          filters: {
            type: JsonObject,
            required: true
          }
        },
        results: {
          batched: Generic,
          errors: [GqlError]
        }
      },
      previewBatchBoxOfficeReportSettlement: {
        arguments: {
          batchBy: {
            type: JsonObject,
            required: true
          },
          filters: {
            type: JsonObject,
            required: true
          }
        },
        results: {
          batches: Generic,
          errors: [GqlError]
        }
      },
      actionBoxOfficeReportSettlements: {
        arguments: {
          ids: {
            type: [ID],
            required: true
          },
          action: {
            type: String,
            required: true
          }
        },
        results: {
          actionResult: Generic,
          errors: [GqlError]
        }
      }
    }
  }

  async settle () {
    if (!_.includes([STATES.PAID, STATES.VOIDED], this.state)) return

    this.action = ACTIONS.SETTLE

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async unsettle () {
    if (!_.includes([STATES.FINALIZED, STATES.VOIDED], this.state)) return

    this.action = ACTIONS.UNSETTLE

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async void () {
    if (!_.includes([STATES.OPEN, STATES.PROCESSING], this.state)) return

    this.action = ACTIONS.VOID

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async batch (batchBy, filters) {
    const results = await executeMutation(
      'batchBoxOfficeReportSettlement',
      {
        batchBy,
        filters
      },
      {
        batched: {
          structure: {
            value: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async previewBatch (batchBy, filters) {
    const results = await executeMutation(
      'previewBatchBoxOfficeReportSettlement',
      {
        batchBy,
        filters
      },
      {
        batches: {
          structure: {
            value: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async actionBoxOfficeReportSettlements (ids, action) {
    if (_.isEmpty(ids)) {
      return {
        errors: [{
          description: 'Must view at least one Box Office Report Settlement'
        }]
      }
    }

    if (!_.includes(ACTIONS.ALL, action)) {
      return {
        errors: [{
          description: `Invalid action: ${action}`
        }]
      }
    }

    const results = await executeMutation(
      'actionBoxOfficeReportSettlements',
      { ids, action },
      {
        actionResult: {
          structure: {
            value: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async voidAll (ids) {
    return await this.actionBoxOfficeReportSettlements(ids, ACTIONS.VOID)
  }

  async settleAll (ids) {
    return await this.actionBoxOfficeReportSettlements(ids, ACTIONS.SETTLE)
  }

  async unsettleAll (ids) {
    return await this.actionBoxOfficeReportSettlements(ids, ACTIONS.UNSETTLE)
  }
}
